import {
  DEFAULT_PERIOD_ROUNDING_PARAMS,
  formatLocalIsoDate,
  getMonthsFromDays,
  getWeeksFromDays,
  type PeriodRoundingParams,
} from '@render/lib/dateCalc'
import {
  DEFAULT_LOSS_PACE_PARAMS,
  getDaysTillGoalDate,
  getGoalDate,
  getMonthsTillGoalDate,
  getWeightLostByWeeksKg,
  type LossPaceParams,
} from '@render/lib/weightCalc'

export type DaysTillGoalDateParams = LossPaceParams
export type EventDateParams = LossPaceParams & PeriodRoundingParams

/**
 * Stored shape of the FITNESS_FUNCTIONS integration's config (the API's
 * `FitnessFunctionsIntegrationConfigDto`). Each function key holds only the
 * parameters an author overrode; everything absent runs on its default.
 */
export interface FitnessFunctionsConfig {
  daysTillGoalDate?: Partial<DaysTillGoalDateParams>
  eventDate?: Partial<EventDateParams>
}

export type FitnessFunctionKey = keyof FitnessFunctionsConfig

export interface FitnessFunctionParamDef {
  key: string
  label: string
  description: string
  unit: string
  min: number
  max: number
  step: number
  fractionDigits: number
}

export interface FitnessFunctionSampleDef {
  key: string
  label: string
  unit: string
  defaultValue: number | null
  min: number
  max: number
  /** Null is a legitimate sample value (e.g. "visitor has not picked a pace"). */
  nullable?: boolean
}

export type FitnessFunctionSample = Record<string, number | null>
export type FitnessFunctionParams = Record<string, number>

export interface FitnessFunctionResult {
  label: string
  variable: string
  value: number | string | null
  unit: string
}

export interface FitnessFunctionDef {
  key: FitnessFunctionKey
  name: string
  summary: string
  /** The formula in terms of the parameter keys. */
  formula: string[]
  defaults: Readonly<FitnessFunctionParams>
  params: FitnessFunctionParamDef[]
  sample: FitnessFunctionSampleDef[]
  /** The formula with the sample and parameter values filled in. */
  explain: (sample: FitnessFunctionSample, params: FitnessFunctionParams) => string[]
  results: (sample: FitnessFunctionSample, params: FitnessFunctionParams) => FitnessFunctionResult[]
}

export interface WeightGoalInput {
  weightKg: number
  targetWeightKg: number
  goalPaceKg?: number
}

export interface EventDateInput extends WeightGoalInput {
  daysUntilEvent: number
}

export interface EventDateResult {
  days: number
  weeks: number
  months: number
  weightLostKg: number
}

/** What the integration writes for the event-date variables. */
export function computeEventDate(
  { daysUntilEvent, weightKg, targetWeightKg, goalPaceKg }: EventDateInput,
  params: EventDateParams,
): EventDateResult {
  const days = Math.max(0, daysUntilEvent)
  return {
    days,
    weeks: getWeeksFromDays(days, params),
    months: getMonthsFromDays(days, params),
    weightLostKg: getWeightLostByWeeksKg({
      weightKg,
      targetWeightKg,
      weeks: days / 7,
      goalPaceKg,
      lossPace: params,
    }),
  }
}

const LOSS_PACE_PARAM_DEFS: FitnessFunctionParamDef[] = [
  {
    key: 'firstWeekMaxLossKg',
    label: 'First-week loss cap',
    description: 'The most the first week can take off, however large the goal.',
    unit: 'kg',
    min: 0,
    max: 20,
    step: 0.5,
    fractionDigits: 1,
  },
  {
    key: 'firstWeekLossDivisor',
    label: 'First-week share divisor',
    description: 'The first week takes off the weight to lose divided by this, up to the cap.',
    unit: '',
    min: 1,
    max: 10,
    step: 0.5,
    fractionDigits: 1,
  },
  {
    key: 'defaultGoalPaceKg',
    label: 'Default weekly pace',
    description: 'Used when the visitor has not picked a goal pace. A picked pace always wins.',
    unit: 'kg/week',
    min: 0.1,
    max: 5,
    step: 0.1,
    fractionDigits: 1,
  },
]

const WEIGHT_SAMPLE_DEFS: FitnessFunctionSampleDef[] = [
  { key: 'weightKg', label: 'Current weight', unit: 'kg', defaultValue: 90, min: 20, max: 400 },
  { key: 'targetWeightKg', label: 'Goal weight', unit: 'kg', defaultValue: 78, min: 20, max: 400 },
  {
    key: 'goalPaceKg',
    label: 'Picked goal pace',
    unit: 'kg/week',
    defaultValue: null,
    min: 0.1,
    max: 5,
    nullable: true,
  },
]

const num = (value: number) => String(Math.round(value * 100) / 100)

const toWeightGoalInput = (sample: FitnessFunctionSample): WeightGoalInput => ({
  weightKg: Number(sample.weightKg),
  targetWeightKg: Number(sample.targetWeightKg),
  goalPaceKg: sample.goalPaceKg && sample.goalPaceKg > 0 ? sample.goalPaceKg : undefined,
})

const lossPaceOf = (params: FitnessFunctionParams): LossPaceParams => ({
  firstWeekMaxLossKg: params.firstWeekMaxLossKg!,
  firstWeekLossDivisor: params.firstWeekLossDivisor!,
  defaultGoalPaceKg: params.defaultGoalPaceKg!,
})

const periodRoundingOf = (params: FitnessFunctionParams): PeriodRoundingParams => ({
  weekRoundUpFromDays: params.weekRoundUpFromDays!,
  daysPerMonth: params.daysPerMonth!,
  monthRoundUpFromDays: params.monthRoundUpFromDays!,
})

const firstWeekLossLine = (input: WeightGoalInput, lossPace: LossPaceParams) => {
  const diff = input.weightKg - input.targetWeightKg
  const loss = Math.min(lossPace.firstWeekMaxLossKg, diff / lossPace.firstWeekLossDivisor)
  return {
    diff,
    loss,
    pace: input.goalPaceKg ?? lossPace.defaultGoalPaceKg,
    text: `min(${num(lossPace.firstWeekMaxLossKg)}, ${num(diff)} ÷ ${num(lossPace.firstWeekLossDivisor)}) = ${num(loss)} kg`,
  }
}

const GAIN_PATH_NOTE =
  'Goal weight is above current weight: the gain pace model applies and these parameters have no effect.'

export const DAYS_TILL_GOAL_DATE_FUNCTION: FitnessFunctionDef = {
  key: 'daysTillGoalDate',
  name: 'getDaysTillGoalDate',
  summary:
    'Days until the visitor reaches their goal weight. targetGoalDate, fasterGoalDate and monthsTillGoalDate are derived from it.',
  formula: [
    'firstWeekLoss = min(firstWeekMaxLossKg, (weight − goal) ÷ firstWeekLossDivisor)',
    'days = ((weight − goal − firstWeekLoss) ÷ pace + 1) × 7',
  ],
  defaults: { ...DEFAULT_LOSS_PACE_PARAMS },
  params: LOSS_PACE_PARAM_DEFS,
  sample: WEIGHT_SAMPLE_DEFS,
  explain: (sample, params) => {
    const input = toWeightGoalInput(sample)
    if (input.targetWeightKg >= input.weightKg) return [GAIN_PATH_NOTE]
    const lossPace = lossPaceOf(params)
    const first = firstWeekLossLine(input, lossPace)
    const days = getDaysTillGoalDate({ ...input, lossPace })
    return [
      `firstWeekLoss = ${first.text}`,
      `days = ((${num(first.diff)} − ${num(first.loss)}) ÷ ${num(first.pace)} + 1) × 7 ≈ ${days}`,
    ]
  },
  results: (sample, params) => {
    const input = { ...toWeightGoalInput(sample), lossPace: lossPaceOf(params) }
    return [
      {
        label: 'Days till goal',
        variable: 'global.daysTillGoalDate',
        value: getDaysTillGoalDate(input),
        unit: 'days',
      },
      {
        label: 'Months till goal',
        variable: 'global.monthsTillGoalDate',
        value: getMonthsTillGoalDate(input),
        unit: 'months',
      },
      {
        label: 'Goal date (from today)',
        variable: 'global.targetGoalDate',
        value: formatLocalIsoDate(getGoalDate(input)),
        unit: '',
      },
    ]
  },
}

export const EVENT_DATE_FUNCTION: FitnessFunctionDef = {
  key: 'eventDate',
  name: 'getEventDate',
  summary:
    'Time from today until the date the visitor picked in global.eventDate, as days / weeks / months, and the kg they are projected to lose by then.',
  formula: [
    'weeks = ⌊days ÷ 7⌋, +1 when the spare days ≥ weekRoundUpFromDays',
    'months = ⌊days ÷ daysPerMonth⌋, +1 when the spare days ≥ monthRoundUpFromDays',
    'kgLost = min(weight − goal, firstWeekLoss + pace × (days ÷ 7 − 1))',
  ],
  defaults: { ...DEFAULT_LOSS_PACE_PARAMS, ...DEFAULT_PERIOD_ROUNDING_PARAMS },
  params: [
    {
      key: 'weekRoundUpFromDays',
      label: 'Round weeks up from',
      description: 'Spare days past a whole week that count as one more week. 7 never rounds up.',
      unit: 'days',
      min: 1,
      max: 7,
      step: 1,
      fractionDigits: 0,
    },
    {
      key: 'daysPerMonth',
      label: 'Days per month',
      description: 'Length of one month when the day count is turned into months.',
      unit: 'days',
      min: 28,
      max: 31,
      step: 1,
      fractionDigits: 0,
    },
    {
      key: 'monthRoundUpFromDays',
      label: 'Round months up from',
      description:
        'Spare days past a whole month that count as one more month. Above days per month never rounds up.',
      unit: 'days',
      min: 1,
      max: 31,
      step: 1,
      fractionDigits: 0,
    },
    ...LOSS_PACE_PARAM_DEFS,
  ],
  sample: [
    {
      key: 'daysUntilEvent',
      label: 'Days until event',
      unit: 'days',
      defaultValue: 45,
      min: 0,
      max: 3650,
    },
    ...WEIGHT_SAMPLE_DEFS,
  ],
  explain: (sample, params) => {
    const days = Math.max(0, Number(sample.daysUntilEvent))
    const rounding = periodRoundingOf(params)
    const result = computeEventDate(
      { ...toWeightGoalInput(sample), daysUntilEvent: days },
      { ...lossPaceOf(params), ...rounding },
    )
    const weekSpare = days % 7
    const monthSpare = days % rounding.daysPerMonth
    const lines = [
      `weeks = ⌊${days} ÷ 7⌋ = ${Math.floor(days / 7)}, spare ${weekSpare} ${weekSpare >= rounding.weekRoundUpFromDays ? '≥' : '<'} ${rounding.weekRoundUpFromDays} → ${result.weeks}`,
      `months = ⌊${days} ÷ ${rounding.daysPerMonth}⌋ = ${Math.floor(days / rounding.daysPerMonth)}, spare ${monthSpare} ${monthSpare >= rounding.monthRoundUpFromDays ? '≥' : '<'} ${rounding.monthRoundUpFromDays} → ${result.months}`,
    ]
    const input = toWeightGoalInput(sample)
    if (input.targetWeightKg >= input.weightKg) return [...lines, GAIN_PATH_NOTE]
    const first = firstWeekLossLine(input, lossPaceOf(params))
    return [
      ...lines,
      `firstWeekLoss = ${first.text}`,
      `kgLost = min(${num(first.diff)}, ${num(first.loss)} + ${num(first.pace)} × ${num(Math.max(0, days / 7 - 1))}) ≈ ${result.weightLostKg}`,
    ]
  },
  results: (sample, params) => {
    const result = computeEventDate(
      { ...toWeightGoalInput(sample), daysUntilEvent: Number(sample.daysUntilEvent) },
      { ...lossPaceOf(params), ...periodRoundingOf(params) },
    )
    return [
      { label: 'Days', variable: 'global.daysUntilEvent', value: result.days, unit: 'days' },
      { label: 'Weeks', variable: 'global.weeksUntilEvent', value: result.weeks, unit: 'weeks' },
      {
        label: 'Months',
        variable: 'global.monthsUntilEvent',
        value: result.months,
        unit: 'months',
      },
      {
        label: 'Lost by the event',
        variable: 'global.weightLostByEventKg',
        value: result.weightLostKg,
        unit: 'kg',
      },
    ]
  },
}

/** Every overridable function, in the order the editor lists them. */
export const FITNESS_FUNCTIONS: FitnessFunctionDef[] = [
  DAYS_TILL_GOAL_DATE_FUNCTION,
  EVENT_DATE_FUNCTION,
]

/**
 * A function's effective parameters: each stored override that is a finite
 * number inside its bounds, the default for everything else.
 */
export function resolveFunctionParams(
  def: FitnessFunctionDef,
  stored: Record<string, unknown> | null | undefined,
): FitnessFunctionParams {
  const params: FitnessFunctionParams = { ...def.defaults }
  for (const param of def.params) {
    const value = stored?.[param.key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value >= param.min && value <= param.max) params[param.key] = value
    }
  }
  return params
}

export const resolveDaysTillGoalDateParams = (
  config: FitnessFunctionsConfig | null | undefined,
): DaysTillGoalDateParams =>
  lossPaceOf(resolveFunctionParams(DAYS_TILL_GOAL_DATE_FUNCTION, config?.daysTillGoalDate))

export const resolveEventDateParams = (
  config: FitnessFunctionsConfig | null | undefined,
): EventDateParams => {
  const params = resolveFunctionParams(EVENT_DATE_FUNCTION, config?.eventDate)
  return { ...lossPaceOf(params), ...periodRoundingOf(params) }
}
